import { useEmergencyAddressDialog } from 'app/components/ListPhoneNumbers/components/PhoneNumberListOptions/components/EmergencyAddressDialog';
import { PhoneNumberDisplay } from 'app/components/PhoneNumberDisplay';
import { QuickFinderExtensionDialog } from 'app/components/QuickFinderExtensionDialog';
import { QuickFinderPhoneNumberDialog } from 'app/components/QuickFinderPhoneNumberDialog';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogInProgress,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InfoTooltip,
  Link,
  TextField,
  Typography,
} from 'app/design';
import { Clear as ClearIcon } from 'app/design/icons-material';
import { useUpdateAccountPartial } from 'app/hooks/mutations/account';
import { useUpdatePhoneNumberPartial } from 'app/hooks/mutations/phoneNumbers';
import { useAccountQuery } from 'app/hooks/queries/account';
import { usePhoneNumberQuery } from 'app/hooks/queries/phoneNumber';
import { useToggleReducer } from 'app/utilities';
import { useMarkdownTranslate } from 'app/utilities/translate';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Account } from 'types/account';
import { PhoneNumber } from 'types/phoneNumber';

// interface for useForm. Not required as it can be inferred but
// - prevents typos and provides a layer of documentation and prevents
// - incorrect registers
interface AccountCallerIdFormFields {
  internalName?: string;
  internalNumber?: string | null;
  externalName?: string;
  externalNumber?: string | null;
  emergencyNumber?: string | null;
  updateExternalName: boolean;
}

// interface declaring which props are required/allowed
interface AccountCallerIdDialogProps {
  onCancel: () => void;
  onComplete: () => void;
}

const CallerIdDialogAccount = ({
  onCancel,
  onComplete,
}: AccountCallerIdDialogProps) => {
  const {
    data: account,
    isLoading: accountIsLoading,
    isError: accountIsError,
  } = useAccountQuery({
    onSuccess: account => {
      setFormDefaults({
        internalName: account?.doc.caller_id?.internal?.name,
        internalNumber: account?.doc.caller_id?.internal?.number,
        externalName: externalNumber?.doc.cnam?.display_name,
        externalNumber: externalNumber?.doc.id,
        emergencyNumber: account?.doc.caller_id?.emergency?.number,
        updateExternalName: false,
      });
    },
  });
  const {
    data: externalNumber,
    isLoading: externalNumberIsLoading,
    error: externalNumberIsError,
  } = usePhoneNumberQuery({ id: account?.doc.caller_id?.external?.number });

  const updateAccount = useUpdateAccountPartial();
  const updatePhoneNumber = useUpdatePhoneNumberPartial();
  const [formDefaults, setFormDefaults] = useState<AccountCallerIdFormFields>({
    internalName: account?.doc.caller_id?.internal?.name,
    internalNumber: account?.doc.caller_id?.internal?.number,
    externalName: externalNumber?.doc.cnam?.display_name,
    externalNumber: externalNumber?.doc.id,
    emergencyNumber: account?.doc.caller_id?.emergency?.number,
    updateExternalName: false,
  });

  const handleSubmit = async (accountForm: AccountCallerIdFormFields) => {
    // update form defaults in case of error
    setFormDefaults(accountForm);

    const {
      externalNumber,
      externalName,
      emergencyNumber,
      internalNumber,
      internalName,
      updateExternalName,
    } = accountForm;

    const updateAccountPromise = updateAccount.mutateAsync({
      id: account?.id!,
      caller_id: {
        internal: {
          name: internalName,
          number: internalNumber,
        },
        external: {
          number: externalNumber,
        },
        emergency: {
          number: emergencyNumber,
        },
      },
    });

    let promises = [updateAccountPromise];

    // need to update phone number external label?
    if (externalNumber && updateExternalName) {
      const updatePhoneNumberPromise = updatePhoneNumber.mutateAsync({
        id: externalNumber,
        cnam: {
          display_name: externalName || undefined,
        },
      });
      promises.push(updatePhoneNumberPromise);
    }

    // resolve promise with toast notifications
    const results = await toast.promise(Promise.all(promises), {
      pending: 'Updating account caller ID...',
      error: 'Failed to update account caller ID.',
      success: 'Account caller ID updated!',
    });

    const errorResponse = results.find(result => result.status !== 'success');

    if (errorResponse) {
      console.log('error', errorResponse);
    } else {
      onComplete();
      // await refetchPhoneNumbers();
    }
  };

  return (
    <Dialog open={true} fullWidth maxWidth={'sm'}>
      {accountIsLoading ||
      (externalNumberIsLoading && !externalNumberIsError) ? (
        <DialogInProgress title={'Loading account caller ID info...'} />
      ) : updateAccount.isLoading || updatePhoneNumber.isLoading ? (
        <DialogInProgress title={'Updating Account Caller ID...'} />
      ) : (
        <>
          <DialogTitle>Edit Account Caller ID</DialogTitle>
          <Divider />
          {account ? (
            <AccountCallerIdForm
              defaultFormValues={formDefaults}
              initialExternalNumber={externalNumber}
              account={account!}
              onCancel={onCancel}
              onSubmit={handleSubmit}
              errorMessage={
                updateAccount.error
                  ? `${updateAccount.error.message} ${
                      updateAccount.error.response
                        ? `: ${updateAccount.error.response?.statusText}`
                        : ''
                    }`
                  : updatePhoneNumber.error
                  ? `${updatePhoneNumber.error.message} ${
                      updatePhoneNumber.error.response
                        ? `: ${updatePhoneNumber.error.response?.statusText}`
                        : ''
                    }`
                  : undefined
              }
            />
          ) : (
            <>
              <DialogContent>
                <Typography color={'error'}>Invalid Account ID.</Typography>
              </DialogContent>
              <Divider />
              <DialogActions sx={{ justifyContent: 'left' }}>
                <Button variant={'outlined'} color={'error'} onClick={onCancel}>
                  Cancel
                </Button>
              </DialogActions>
            </>
          )}
        </>
      )}
    </Dialog>
  );
};

interface AccountCallerIdFormProps {
  defaultFormValues: AccountCallerIdFormFields;
  account: Account;
  initialExternalNumber?: PhoneNumber;
  onCancel: () => void;
  onSubmit: (formValues: AccountCallerIdFormFields) => void;
  errorMessage?: string;
  startDirty?: boolean;
}

const AccountCallerIdForm = ({
  account,
  defaultFormValues,
  onCancel,
  onSubmit,
  errorMessage,
  startDirty = false,
}: AccountCallerIdFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isDirty, dirtyFields },
    setError,
    watch,
    control,
    setValue,
  } = useForm<AccountCallerIdFormFields>({
    defaultValues: defaultFormValues,
  });

  const formWatch = watch();

  const { data: externalNumber, isLoading: externalNumberIsLoading } =
    usePhoneNumberQuery({ id: formWatch.externalNumber });

  const {
    data: emergencyNumber,
    isLoading: emergencyNumberIsLoading,
    refetch: refetchEmergencyNumber,
  } = usePhoneNumberQuery({ id: formWatch.emergencyNumber });

  const {
    Dialog: EmergencyAddressDialog,
    DialogProps: EmergencyAddressProps,
    toggleOpen: toggleEmergencyAddressDialog,
  } = useEmergencyAddressDialog();

  useEffect(() => {
    setValue('externalName', externalNumber?.doc?.cnam?.display_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalNumber]);

  // flag whether to update external number display name
  useEffect(() => {
    if (dirtyFields.externalName) {
      setValue('updateExternalName', true);
    } else {
      setValue('updateExternalName', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirtyFields.externalName]);

  const handleSetInternal = (number: string | null) => {
    setValue('internalNumber', number, { shouldDirty: true });
  };

  const handleSetExternal = (number: string | null) => {
    setValue('externalNumber', number, { shouldDirty: true });
  };

  const handleSetEmergency = (number: string | null) => {
    setValue('emergencyNumber', number, { shouldDirty: true });
  };

  const { t } = useMarkdownTranslate();

  return (
    <>
      <DialogContent>
        <>
          {/* Internal CID */}
          <Grid container spacing={1}>
            <Grid item>
              <Typography variant={'h6'}>Internal</Typography>
            </Grid>
            <Grid item>
              <InfoTooltip
                size={'large'}
                title={t('caller_id.internal.tooltip')}
              />
            </Grid>
          </Grid>
          <br />
          <Grid container spacing={2}>
            <Grid item md={4}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label={'Name'}
                placeholder={'leave empty'}
                {...register('internalName')}
                // helperText={
                //   formWatch.internalName
                //     ? undefined
                //     : account.doc.caller_id?.internal?.name
                //     ? `Will use internal account name:
                //      ${account.doc.caller_id?.internal?.name}
                //     `
                //     : null
                // }
              />
            </Grid>
            <Grid item md={5}>
              <div style={{ position: 'relative' }}>
                <TextField
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label={'Number'}
                  {...register('internalNumber')}
                  // disabled
                  // value={formWatch.internalNumber ? '' : 'Select number'}
                  // helperText={
                  //   formWatch.internalNumber ? undefined : account.doc.caller_id
                  //       ?.internal?.number ? (
                  //     <Grid container>
                  //       <Grid item>Will use internal account number:</Grid>
                  //       <Grid item>
                  //         {/* @ts-ignore */}
                  //         <PhoneNumberDisplay
                  //           ptn={account.doc.caller_id?.internal?.number}
                  //         />
                  //       </Grid>
                  //     </Grid>
                  //   ) : undefined
                  // }
                />

                {/* internal number display */}
                {/* {formWatch.internalNumber ? (
                  <div style={{ position: 'absolute', top: 9, left: 15 }}>
                    <Grid container alignItems={'center'}>
                      <Grid item>
                        <Typography variant={'h6'}>
                           @ts-ignore
                          <PhoneNumberDisplay ptn={formWatch.internalNumber} />
                        </Typography>
                      </Grid>
                      <Grid item>
                        <IconButton onClick={() => handleSetInternal(null)}>
                          <ClearIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                ) : null}*/}
              </div>
            </Grid>

            {/* Links to select ext/number for internal number */}
            <Grid item md={3}>
              <NumberSelect
                number={formWatch.internalNumber}
                onSelect={number => handleSetInternal(number)}
              />
              <ExtensionSelect
                ext={formWatch.internalNumber}
                onSelect={ext => handleSetInternal(ext)}
              />
            </Grid>
          </Grid>
          <br />
          {/* External CID */}
          <Grid container spacing={1}>
            <Grid item>
              <Typography variant={'h6'}>External</Typography>
            </Grid>
            <Grid item>
              <InfoTooltip
                size={'large'}
                title={t('caller_id.external.tooltip')}
              />
            </Grid>
          </Grid>
          <br />
          <Grid container spacing={2}>
            <Grid item md={4}>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label={'Name'}
                placeholder={
                  externalNumberIsLoading
                    ? 'Loading'
                    : formWatch.externalNumber
                    ? 'leave empty'
                    : 'select number'
                }
                {...register('externalName')}
                error={!!formErrors.externalName}
                disabled={externalNumberIsLoading || !formWatch.externalNumber}
                helperText={
                  '*Will change name for this number in all locations'
                }
              />
            </Grid>
            <Grid item md={5}>
              <div style={{ position: 'relative' }}>
                <TextField
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label={'Number'}
                  disabled
                  value={formWatch.externalNumber ? '' : 'Select number'}
                  // helperText={
                  //   formWatch.externalNumber ? undefined : account.doc.caller_id
                  //       ?.external?.number ? (
                  //     <Grid container>
                  //       <Grid item>Will use external account number:</Grid>
                  //       <Grid item>
                  //         {/* @ts-ignore */}
                  //         <PhoneNumberDisplay
                  //           ptn={account.doc.caller_id?.external?.number}
                  //         />
                  //       </Grid>
                  //     </Grid>
                  //   ) : undefined
                  // }
                />

                {/* external number display */}
                {formWatch.externalNumber ? (
                  <div style={{ position: 'absolute', top: 9, left: 15 }}>
                    <Grid container alignItems={'center'}>
                      <Grid item>
                        <Typography variant={'h6'}>
                          {/* @ts-ignore*/}
                          <PhoneNumberDisplay ptn={formWatch.externalNumber} />
                        </Typography>
                      </Grid>
                      <Grid item>
                        <IconButton onClick={() => handleSetExternal(null)}>
                          <ClearIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                ) : null}
              </div>
            </Grid>
            {/* Links to select number for external number */}
            <Grid item md={3}>
              <NumberSelect
                number={formWatch.externalNumber}
                onSelect={number => handleSetExternal(number)}
              />
            </Grid>
          </Grid>
          <br />
          {/* Emergency CID */}
          <Grid container spacing={1}>
            <Grid item>
              <Typography variant={'h6'}>Emergency</Typography>
            </Grid>
            <Grid item>
              <InfoTooltip
                size={'large'}
                title={t('caller_id.emergency.tooltip')}
              />
            </Grid>
          </Grid>
          <br />
          <Grid container spacing={2}>
            <Grid item md={5}>
              <div style={{ position: 'relative' }}>
                <TextField
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label={'Number'}
                  disabled
                  value={formWatch.emergencyNumber ? '' : 'Select number'}
                  // helperText={
                  //   formWatch.emergencyNumber ? undefined : account.doc
                  //       .caller_id?.emergency?.number ? (
                  //     <Grid container>
                  //       <Grid item>Will use emergency account number:</Grid>
                  //       <Grid item>
                  //         {/* @ts-ignore */}
                  //         <PhoneNumberDisplay
                  //           ptn={account.doc.caller_id?.emergency?.number}
                  //         />
                  //       </Grid>
                  //     </Grid>
                  //   ) : undefined
                  // }
                />

                {/* emergency number display */}
                {formWatch.emergencyNumber ? (
                  <div style={{ position: 'absolute', top: 9, left: 15 }}>
                    <Grid container alignItems={'center'}>
                      <Grid item>
                        <Typography variant={'h6'}>
                          {/* @ts-ignore*/}
                          <PhoneNumberDisplay ptn={formWatch.emergencyNumber} />
                        </Typography>
                      </Grid>
                      <Grid item>
                        <IconButton onClick={() => handleSetEmergency(null)}>
                          <ClearIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                ) : null}
              </div>
            </Grid>
            {/* Links to select number for emergency number */}
            <Grid item md={3}>
              <NumberSelect
                number={formWatch.emergencyNumber}
                onSelect={number => handleSetEmergency(number)}
              />
            </Grid>
            {formWatch.emergencyNumber ? (
              <Grid item md={3}>
                <EmergencyAddressDialog
                  {...EmergencyAddressProps}
                  onComplete={() => {
                    refetchEmergencyNumber();
                    toggleEmergencyAddressDialog();
                  }}
                  onCancel={toggleEmergencyAddressDialog}
                  number={formWatch.emergencyNumber}
                />
                <Button onClick={toggleEmergencyAddressDialog}>
                  Change Address
                </Button>
              </Grid>
            ) : null}
          </Grid>
        </>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button variant={'outlined'} color={'error'} onClick={onCancel}>
          Cancel
        </Button>
        {errorMessage ? (
          <Typography color={'error'}>{errorMessage}</Typography>
        ) : null}
        <Button
          variant={'outlined'}
          color={'success'}
          disabled={
            externalNumberIsLoading || startDirty ? !startDirty : !isDirty
          }
          onClick={handleSubmit(onSubmit)}
        >
          Update
        </Button>
      </DialogActions>
    </>
  );
};

const NumberSelect = ({ number, onSelect }) => {
  const [showDialog, toggleShowDialog] = useToggleReducer(false);

  const handleSelect = (selected: string[]) => {
    const [number] = selected;
    onSelect(number);
    toggleShowDialog();
  };

  const handleClear = () => {
    onSelect(null);
    toggleShowDialog();
  };

  return (
    <div>
      <QuickFinderPhoneNumberDialog
        open={showDialog}
        onSelect={handleSelect}
        onCancel={toggleShowDialog}
        onClear={handleClear}
        initialSelected={[number]}
        selectionTitle={number}
      />
      <Link underline={'hover'} onClick={toggleShowDialog} variant={'caption'}>
        Select Number
      </Link>
    </div>
  );
};

const ExtensionSelect = ({ ext, onSelect }) => {
  const [showDialog, toggleShowDialog] = useToggleReducer(false);

  const handleSelect = (selected: string[]) => {
    const [ext] = selected;
    onSelect(ext);
    toggleShowDialog();
  };

  const handleClear = () => {
    onSelect(null);
    toggleShowDialog();
  };

  return (
    <div>
      <QuickFinderExtensionDialog
        open={showDialog}
        onSelect={handleSelect}
        onCancel={toggleShowDialog}
        onClear={handleClear}
        initialSelected={[ext]}
        selectionTitle={ext}
      />
      <Link underline={'hover'} onClick={toggleShowDialog} variant={'caption'}>
        Select Ext
      </Link>
    </div>
  );
};

export default CallerIdDialogAccount;
